import Swal from "sweetalert2";

const FX_SWAL_CLASS = {
  container: "swal2-on-top",
  popup: "swal-fx-confirm swal-fx-confirm-order",
};

/**
 * Skip show/hide animations + avoid body scrollbar padding (layout reflow) for snappy open/close.
 */
const SWAL_SNAPPY = {
  scrollbarPadding: false,
  showClass: {
    backdrop: "swal2-noanimation",
    popup: "swal2-noanimation",
    icon: "swal2-noanimation",
  },
  hideClass: {
    backdrop: "swal2-noanimation",
    popup: "swal2-noanimation",
    icon: "swal2-noanimation",
  },
};

function escHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/** Confirm Order table — `label (userId)` when both exist (e.g. `5363 (14251710)`). */
export function formatOrderConfirmClientCell(client) {
  if (!client || typeof client !== "object") return "—";
  const idStr = client.value != null && String(client.value).trim() !== "" ? String(client.value).trim() : "";
  const nameStr = client.label != null && String(client.label).trim() !== "" ? String(client.label).trim() : "";
  if (nameStr && idStr) return `${nameStr} (${idStr})`;
  if (idStr) return idStr;
  if (nameStr) return nameStr;
  return "—";
}

/** Legacy-style bordered grid (Lot / Qty / Price / Client / Order type). */
function orderSummaryTable(rows) {
  const body = rows
    .filter(([, v]) => v != null && String(v).trim() !== "")
    .map(
      ([label, val]) =>
        `<tr><td>${escHtml(label)}</td><td>${escHtml(String(val))}</td></tr>`
    )
    .join("");
  return `<table class="fx-swal-order-grid" role="presentation">${body}</table>`;
}

/**
 * Quick order — legacy “Confirm Order” modal: table + Cancel (red) / Place Order (green).
 */
export function confirmQuickOrder({
  lot,
  qty,
  side,
  price,
  clientId,
  clientName,
  orderTypeLabel,
}) {
  const sideStr =
    side === "BUY" ? "BUY" : side === "SELL" ? "SELL" : String(side || "").trim();
  const typeLine =
    orderTypeLabel && sideStr
      ? `${orderTypeLabel} · ${sideStr}`
      : orderTypeLabel || sideStr || "—";
  const clientCell = formatOrderConfirmClientCell({ value: clientId, label: clientName });

  const rows = [
    ["Lot", lot],
    ["Qty", qty],
    ["Price", price],
    ["Client", clientCell],
    ["Order Type", typeLine],
  ];

  return Swal.fire({
    title: "Confirm Order",
    icon: false,
    html: orderSummaryTable(rows),
    showCancelButton: true,
    focusConfirm: true,
    confirmButtonText: "Place Order",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#16a34a",
    cancelButtonColor: "#f43f5e",
    width: "min(92vw, 400px)",
    customClass: FX_SWAL_CLASS,
    buttonsStyling: true,
    reverseButtons: false,
    allowEnterKey: true,
    returnFocus: false,
    ...SWAL_SNAPPY,
  });
}

/** Close position — table summary + Cancel / confirm (gold accent, legacy). */
export function confirmClosePosition({ lot, qty, price }) {
  const rows = [
    ["Lot", lot],
    ["Qty", qty],
    ["Price", price],
  ];
  return Swal.fire({
    title: "Confirm Close",
    icon: false,
    html: orderSummaryTable(rows),
    showCancelButton: true,
    focusCancel: true,
    confirmButtonText: "Yes, Close it!",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#885b24",
    cancelButtonColor: "#f43f5e",
    width: "min(92vw, 380px)",
    customClass: {
      container: "swal2-on-top",
      popup: "swal-fx-confirm swal-fx-confirm-order swal-fx-confirm-close",
    },
    buttonsStyling: true,
    ...SWAL_SNAPPY,
  });
}

export function confirmRolloverPositions(count) {
  const n = Number(count) || 0;
  return Swal.fire({
    title: "Confirm Rollover",
    icon: false,
    text: n === 1 ? "Rollover this position?" : `Rollover ${n} selected positions?`,
    showCancelButton: true,
    focusCancel: true,
    confirmButtonText: "Yes, Rollover it!",
    cancelButtonText: "Cancel",
    confirmButtonColor: "#885b24",
    cancelButtonColor: "#f43f5e",
    customClass: {
      container: "swal2-on-top",
      popup: "swal-fx-confirm swal-fx-confirm-order",
    },
    buttonsStyling: true,
    width: "min(92vw, 380px)",
    ...SWAL_SNAPPY,
  });
}

export function alertSelectPositionFirst() {
  return Swal.fire({
    title: "No selection",
    text: "Please select at least one position.",
    icon: false,
    confirmButtonText: "OK",
    confirmButtonColor: "#16a34a",
    customClass: {
      container: "swal2-on-top",
      popup: "swal-fx-confirm",
    },
    ...SWAL_SNAPPY,
  });
}
